/* eslint-disable react/prop-types */
import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Eye } from "lucide-react";
import hasPermission from "@/hooks/hasPermission";

function StatusPill({ status }) {
  const statusClass =
    status === "Completed"
      ? "text-cgreen"
      : status === "Pre-order"
        ? "text-cyellow"
        : "text-cyellow";

  return <span className={`font-medium ${statusClass}`}>{status}</span>;
}

function OrderListTable({
  orders,
  selectedOrderIds,
  allVisibleSelected,
  onToggleVisibleSelection,
  onToggleOrderSelection,
  onOpenCheckout,
  onOpenNotes,
  onOpenShare,
}) {
  const selectedCount = useMemo(
    () => selectedOrderIds.length,
    [selectedOrderIds],
  );

  return (
    <div className="">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="">
            <TableHead className="w-12 px-3">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={onToggleVisibleSelection}
                className=""
              />
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Date
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Customer
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Order ID
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Order Type
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Discount
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Order Price
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Loyalty
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Till
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Notes
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Status
            </TableHead>
            <TableHead className="text-sm font-medium text-white">
              Operator
            </TableHead>
            <TableHead className="text-right text-sm font-medium text-white">
              Action
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow className="border-gray-800">
              <TableCell
                colSpan={13}
                className="py-8 text-center text-sm text-cwhite3"
              >
                No order found
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order) => {
              const isSelected = selectedOrderIds.includes(order.id);

              return (
                <TableRow
                  key={order.id}
                  className={` text-sm text-cwhite3 transition-colors  ${isSelected ? "bg-[#242019]" : "bg-[#1f1f1f]"
                    }`}
                >
                  <TableCell className="px-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) =>
                        onToggleOrderSelection(order.id, Boolean(checked))
                      }
                      className="border-gray-500 data-[state=checked]:border-cyellow data-[state=checked]:bg-cyellow data-[state=checked]:text-black"
                    />
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.dateLabel}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.customerName}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.orderId}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.orderType}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.discount}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.orderPrice}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.loyalty}
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.till}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => onOpenNotes(order)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-cwhite3 transition hover:bg-white/5 hover:text-white"
                      aria-label={`View notes for ${order.orderId}`}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </TableCell>
                  <TableCell className="text-[13px]">
                    <StatusPill status={order?.status} />
                  </TableCell>
                  <TableCell className="text-[13px] text-cwhite3">
                    {order?.operator}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => onOpenCheckout(order)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-cwhite3 transition hover:bg-white/5 hover:text-white"
                        aria-label={`Checkout ${order.orderId}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {
                        hasPermission("back_office.till_to_till_share.share_till") && (<Button
                          type="button"
                          variant="ghost"
                          onClick={() => onOpenShare([order])}
                          className="h-8 w-8 p-0 text-cgreen hover:bg-cgreen/10 hover:text-cgreen"
                          aria-label={`Share ${order.orderId}`}
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                        </Button>)
                      }
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {selectedCount > 0 && (
        <div className="border-t border-gray-800 px-4 py-3 text-sm text-cwhite3">
          {selectedCount} order{selectedCount > 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );
}

export default OrderListTable;
